// ts nocheck is required because MediaComponentName is dynamic
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { FC, Suspense, lazy } from 'react';
import { upperFirst } from 'lodash-es';
import type {
    Panel as PanelFields,
    Animation as AnimationFields,
    Diagram as DiagramFields,
    Drawing as DrawingFields,
    Photo as PhotoFields,
    Text as TextFields,
} from '@/generated/graphql';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import Animation from '@/components/media/Animation/Animation';
import Diagram from '@/components/media/Diagram/Diagram';
import Drawing from '@/components/media/Drawing/Drawing';
import Photo from '@/components/media/Photo/Photo';
import Text from '@/components/media/Text/Text';
import type { EditConfigMedia } from '@/components/edit/EditShell/EditShell';
import { ImageSize } from '@/utils';
const PanelEdit = lazy(
    () => import(/* webpackChunkName: "edit-main" */ './PanelEdit'),
);
const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);

export type PanelKind = 'animation' | 'diagram' | 'drawing' | 'photo' | 'text';

export const panelKinds: PanelKind[] = [
    'text',
    'photo',
    'animation',
    'diagram',
    'drawing',
];
export const getPanelKind = (panel: PanelFields): PanelKind => {
    return panelKinds.find((kind) => panel[kind].length);
};

type Fields =
    | AnimationFields
    | DiagramFields
    | DrawingFields
    | PhotoFields
    | TextFields;

export type PanelProps = {
    panel: PanelFields;
    panelStyles?: React.CSSProperties;
    imageSize?: ImageSize;
    className?: string;
    index?: number; // TODO: here only for development info purposes.
};

const Panel: FC<PanelProps> = ({
    panel,
    panelStyles,
    imageSize = 'm',
    className,
    index,
}) => {
    /** (!) panelStyles are used when the ClusterShape === "FLOAT".
     *      When not FLOAT then panelStyles = {} empty object
     *      And never when <Panel/> is used from banner.tsx
     *      When panelStyles is undefined.
     */

    const { userCurrent } = useUserCurrent();

    /** (!) mediaComponents needs to be inside the component function!
     *      I suspect otherwise there is a race condition.
     *      Some minor changes else where causes the imported media components
     *      like Animation to be undefined.
     */
    const mediaComponents = {
        animation: Animation,
        diagram: Diagram,
        drawing: Drawing,
        photo: Photo,
        text: Text,
    };

    const kind: PanelKind = getPanelKind(panel);
    const fields: Fields = panel[kind][0];
    const MediaComponentName = mediaComponents[kind];

    const panelContent = (
        <div
            className={`Panel${className ? ` ${className}` : ''}`}
            {...(panelStyles && Object.keys(panelStyles).length
                ? { style: panelStyles }
                : {})}
            data-index={index}
            {...(userCurrent?.isContentEditor
                ? { title: `${panel.adminTitle} (${panel.id})` }
                : {})}
        >
            <MediaComponentName
                fields={fields}
                panelOwnerId={panel.owner.id}
                imageSize={imageSize}
            ></MediaComponentName>
        </div>
    );

    if (userCurrent?.isEditMode === 'PANEL') {
        const passEditState = {
            editKey: panel.id,
            componentType: 'PANEL',
            fields: {
                adminTitle: panel.adminTitle,
                adminNote: panel.adminNote,
                published: panel.published,
            },
            localFields: {
                x: panel.x,
                y: panel.y,
                z: panel.z,
            },
            panelId: panel.id,
            ownerId: panel.owner.id,
        };
        return (
            <Suspense fallback={panelContent}>
                <EditShell passEditState={passEditState}>
                    <PanelEdit media={upperFirst(kind) as EditConfigMedia}>
                        {panelContent}
                    </PanelEdit>
                </EditShell>
            </Suspense>
        );
    }

    return panelContent;
};

export default Panel;
